import React from 'react';
import {
  Card,
  CardContent,
  Stack,
  Divider,
  Typography,
  Box,
  Skeleton,
  Button,
  CircularProgress,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { usePermission } from '@billda/permissions-ui';
import { SiteCardHeader } from '../molecules/SiteCardHeader';
import { SiteMetricsDisplay } from '../molecules/SiteMetricsDisplay';
import { FinancialSummaryRow } from '../molecules/FinancialSummaryRow';
import { ProgressBar } from '../atoms/ProgressBar';

export interface SiteDetailsHeaderProps {
  site: {
    id: number;
    name: string;
    slug: string;
    what3WordsAddress: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  financialMetrics?: {
    totalLabourPrice: number;
    approvedAmount: number;
    bookedAmount: number;
    remaining: number;
    progress: number;
  } | null;
  plotCount?: number;
  isLoading?: boolean;
  onBackfillCoordinates?: () => void;
  isBackfilling?: boolean;
}

export const SiteDetailsHeader: React.FC<SiteDetailsHeaderProps> = ({
  site,
  financialMetrics,
  plotCount = 0,
  isLoading = false,
  onBackfillCoordinates,
  isBackfilling = false,
}) => {
  const hasImportPermission = usePermission(Permissions.Sites.Import);

  // Check if backfill should show: coordinates missing + has import permission + handler provided
  const showBackfill =
    site.latitude == null &&
    hasImportPermission &&
    Boolean(onBackfillCoordinates);

  return (
    <Card
      data-testid="site-header-strip"
      sx={{
        width: '100%',
      }}
    >
      <CardContent sx={{ py: 1.5 }}>
        <Stack spacing={1}>
          {/* Site name and What3Words - always visible */}
          {isLoading ? (
            <Box>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={24} />
            </Box>
          ) : (
            <SiteCardHeader
              name={site.name}
              slug={site.slug}
              what3WordsAddress={site.what3WordsAddress || undefined}
              latitude={site.latitude}
              longitude={site.longitude}
              compactDirections
              actions={null}
            />
          )}

          {showBackfill && (
            <Button
              size="small"
              variant="text"
              startIcon={
                isBackfilling ? (
                  <CircularProgress size={16} />
                ) : (
                  <MyLocationIcon fontSize="small" />
                )
              }
              onClick={onBackfillCoordinates}
              disabled={isBackfilling}
              data-testid="backfill-coordinates-button"
              sx={{ alignSelf: 'flex-start' }}
            >
              Backfill Coordinates
            </Button>
          )}

          <Divider />

          {/* Financial metrics - permission-gated */}
          <PermissionGate permission="sites.financial.read">
            {financialMetrics ? (
              <Stack spacing={1}>
                {/* Total Value and Plot Count */}
                {isLoading ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </Box>
                  </Box>
                ) : (
                  <SiteMetricsDisplay
                    totalLabourPrice={financialMetrics.totalLabourPrice}
                    plotCount={plotCount}
                  />
                )}

                {/* Progress Bar */}
                {isLoading ? (
                  <Box>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={8} />
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Progress
                    </Typography>
                    <ProgressBar
                      percentage={financialMetrics.progress}
                      variant="full-width"
                      size="large"
                    />
                  </Box>
                )}

                {/* Financial Summary Row */}
                {isLoading ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </Box>
                  </Box>
                ) : (
                  <FinancialSummaryRow
                    approved={financialMetrics.approvedAmount}
                    booked={financialMetrics.bookedAmount}
                    remaining={financialMetrics.remaining}
                  />
                )}
              </Stack>
            ) : null}
          </PermissionGate>
        </Stack>
      </CardContent>
    </Card>
  );
};
